/**
 * RestoreConfirmModal Component (AC-571)
 *
 * Modal for confirming checkpoint restoration. Displays checkpoint information,
 * entity counts, and allows the user to create a pre-restore checkpoint.
 *
 * Parent Component: CheckpointList
 *
 * API Contract:
 * - restoreCheckpoint(projectId: number, checkpointId: number, createPreRestore?: boolean) -> Promise<void>
 */

import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { useVersionStore } from '../../store/versionStore';
import { CheckpointSummary } from '../../types/version_control';

interface RestoreConfirmModalProps {
  isOpen: boolean;
  checkpoint: CheckpointSummary | null;
  projectId: number;
  onClose: () => void;
  onRestoreComplete: () => void;
}

const RestoreConfirmModal: React.FC<RestoreConfirmModalProps> = ({
  isOpen,
  checkpoint,
  projectId,
  onClose,
  onRestoreComplete,
}) => {
  const [createPreRestoreCheckpoint, setCreatePreRestoreCheckpoint] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const { restoreCheckpoint } = useVersionStore();

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setCreatePreRestoreCheckpoint(true);
      setRestoring(false);
      setRestoreError(null);
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    if (!checkpoint) return;

    setRestoring(true);
    setRestoreError(null);

    try {
      await restoreCheckpoint(projectId, checkpoint.checkpoint_id, createPreRestoreCheckpoint);
      onRestoreComplete();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to restore checkpoint';
      setRestoreError(message);
    } finally {
      setRestoring(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen || !checkpoint) {
    return null;
  }

  // Format the date
  const createdDate = new Date(checkpoint.created_at);
  const formattedDate = createdDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  // Determine display name
  const displayName = checkpoint.name || 'Auto-checkpoint';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-labelledby="restore-modal-title"
        aria-modal="true"
        className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-lg w-full"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 id="restore-modal-title" className="text-xl font-semibold text-gray-900 dark:text-warm-white">
            Restore Checkpoint
          </h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Warning message */}
          <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              This action will overwrite all project data with the checkpoint snapshot. Any unsaved changes will be lost.
            </p>
          </div>

          {/* Checkpoint info */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Checkpoint Details</h3>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Name:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-warm-white">{displayName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Created:</span>
                <span className="text-sm text-gray-900 dark:text-warm-white">{formattedDate}</span>
              </div>
            </div>
          </div>

          {/* Entity counts */}
          {checkpoint.entity_counts && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Entities to Restore</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-primary-50 rounded-lg p-3 text-center">
                  <span className="text-sm text-blue-700">{checkpoint.entity_counts.models} models</span>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <span className="text-sm text-green-700">{checkpoint.entity_counts.agents} agents</span>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <span className="text-sm text-purple-700">{checkpoint.entity_counts.orchestrations} orchestrations</span>
                </div>
              </div>
            </div>
          )}

          {/* Pre-restore checkpoint option */}
          <div className="flex items-start gap-3 pt-2">
            <input
              id="create-pre-restore"
              type="checkbox"
              checked={createPreRestoreCheckpoint}
              onChange={(e) => setCreatePreRestoreCheckpoint(e.target.checked)}
              className="mt-1 h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
            />
            <label htmlFor="create-pre-restore" className="text-sm text-gray-700 dark:text-gray-300">
              Create a pre-restore checkpoint
              <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Save your work before restoring, allowing you to undo if needed
              </span>
            </label>
          </div>

          {/* Error message */}
          {restoreError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600">{restoreError}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleCancel}
            disabled={restoring}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={restoring}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {restoring ? 'Restoring...' : 'Restore'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestoreConfirmModal;
